import type React from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  animate,
  AnimatePresence,
  motion,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import { ChevronLeft, ChevronRight, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TennisBall } from '@/assets/icons/TennisBall';
import type { PlayerStatistics } from '@/types/statistics';
import { RESULT_COLORS, type CourtSide } from './SmartCourt';
import type { PlayerColor } from './playerColors';
import {
  SHOT_TYPES,
  classifyShot,
  selectShots,
  isServeType,
  chipForType,
} from './shotTypes';

/*
 * In-place shot simulator.
 *
 * Replaces the report's landscape court while active: the field rotates into a
 * natively-portrait court (header stays above so the player can still be
 * changed), one shot is shown at a time, and the Movimento/Pancadas tab bar is
 * swapped for the shot-type chips + step arrows. Each shot draws the hitter's
 * icon, a per-shot-type ball trajectory crossing the net, and the outcome
 * marker. Closing rotates the field back.
 *
 * Data note: the payload carries only the *hitter's* position per shot, so the
 * simulator shows the striker alone.
 */

// Portrait court: lateral (width) 100u = 10m, depth (length) 200u = 20m, with a
// 10u margin. The net is the horizontal line down the middle.
const M = 10;
const CW = 100;
const VW = 120;
const VH = 220;
const NET_Y = 110;

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

// Lateral x ∈ [-5,5] in the player's OWN perspective. The 'right' team faces the
// other way (a 180° rotation flips both axes), so we negate lateral for them —
// mirroring what ownDepthY does for depth. Without it the right team's left/right
// renders flipped.
const latX = (side: CourtSide, x: number) =>
  M + (((side === 'right' ? -1 : 1) * clamp(x, -5, 5) + 5) / 10) * CW;
// 'left' team plays the BOTTOM half, 'right' the TOP — mirroring the landscape
// court split. Own depth runs net→back wall; opponent depth is its mirror.
const ownDepthY = (side: CourtSide, y: number) =>
  side === 'left'
    ? NET_Y + (clamp(y, 0, 10) / 10) * 100
    : NET_Y - (clamp(y, 0, 10) / 10) * 100;
const oppDepthY = (side: CourtSide, y: number) =>
  side === 'left'
    ? NET_Y - (clamp(y, 0, 10) / 10) * 100
    : NET_Y + (clamp(y, 0, 10) / 10) * 100;

// Per shot-type flight profile. `bulge` is the arc height as a fraction of the
// trajectory length (≈0 → straight); `duration` is flight time (lower = faster);
// `ease` shapes the pace. Smashes/overheads fly straight and fast; groundstrokes
// loop higher and slower; volleys are flat and quick; serves sit in between.
const SHOT_PROFILE: Record<
  string,
  { bulge: number; duration: number; ease: string }
> = {
  smash: { bulge: 0.015, duration: 0.3, ease: 'easeIn' },
  overhead: { bulge: 0.03, duration: 0.34, ease: 'easeIn' },
  rally_server: { bulge: 0.09, duration: 0.46, ease: 'easeOut' },
  forehand_volley: { bulge: 0.07, duration: 0.4, ease: 'easeOut' },
  backhand_volley: { bulge: 0.07, duration: 0.4, ease: 'easeOut' },
  forehand: { bulge: 0.22, duration: 0.72, ease: 'easeInOut' },
  backhand: { bulge: 0.22, duration: 0.72, ease: 'easeInOut' },
};
const DEFAULT_PROFILE = { bulge: 0.16, duration: 0.6, ease: 'easeInOut' };

export interface SimPlayer {
  id: string;
  name: string;
  color: PlayerColor;
  crop?: string;
  ps: PlayerStatistics;
  side: CourtSide;
}

type ShotEntry = NonNullable<
  PlayerStatistics['shots_tracking']
>['shot_outcomes'][string][number];

interface SimShot {
  shot: ShotEntry;
  type: string;
}

// Flatten one player's shots of the chosen type, tagging each with its own type
// (so 'all' still picks the right flight profile per shot), ordered by frame.
function buildShots(ps: PlayerStatistics, shotType: string): SimShot[] {
  const so = ps.shots_tracking?.shot_outcomes;
  if (!so) return [];
  // Same selection as selectShots (accepted, no serves, smash grouped under
  // overhead), but each shot keeps its real backend type for the flight profile.
  const out: SimShot[] = [];
  for (const [type, arr] of Object.entries(so)) {
    if (isServeType(type)) continue;
    if (shotType !== 'all' && chipForType(type) !== shotType) continue;
    for (const shot of arr) if (shot.accepted) out.push({ shot, type });
  }
  return out.sort((a, b) => a.shot.shot_frame - b.shot.shot_frame);
}

function countOf(ps: PlayerStatistics, type: string): number {
  return selectShots(ps, type).length;
}

// ── one shot drawn on the court ────────────────────────────────────────────
const ShotArc: React.FC<{
  sim: SimShot;
  side: CourtSide;
  teamColor: string;
}> = ({ sim, side, teamColor }) => {
  const { shot, type } = sim;
  const profile = SHOT_PROFILE[type] ?? DEFAULT_PROFILE;

  const contactSrc = shot.shot_position_mirroed;
  const contact = {
    x: latX(side, contactSrc?.x ?? shot.player_position_mirroed.x),
    y: ownDepthY(side, contactSrc?.y ?? shot.player_position_mirroed.y),
  };
  const land = shot.outcome.position_mirroed;
  const hasLanding = !!land && land.x != null && land.y != null;
  const landing = hasLanding
    ? {
        x: latX(side, land!.x as number),
        y: oppDepthY(side, land!.y as number),
      }
    : null;

  const pathRef = useRef<SVGPathElement | null>(null);
  const progress = useMotionValue(0);

  // The trail (pathLength) and the ball (getPointAtLength) are BOTH read from
  // the same `progress` in arc-length terms, so the drawn line always ends
  // exactly at the ball — it can never run ahead of it. At progress 0 the
  // pathLength is 0, so nothing is drawn (no initial full-line flash).
  const ballX = useTransform(progress, (p) => {
    const path = pathRef.current;
    return path
      ? path.getPointAtLength(p * path.getTotalLength()).x
      : contact.x;
  });
  const ballY = useTransform(progress, (p) => {
    const path = pathRef.current;
    return path
      ? path.getPointAtLength(p * path.getTotalLength()).y
      : contact.y;
  });
  const ballOpacity = useTransform(progress, [0, 0.08, 0.9, 1], [0, 1, 1, 0]);

  useLayoutEffect(() => {
    progress.set(0);
    const controls = animate(progress, 1, {
      duration: profile.duration,
      ease: profile.ease as never,
    });
    return () => controls.stop();
    // Re-run for each new shot (frame is unique per shot).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shot.shot_frame, type]);

  if (!landing) {
    return (
      <circle
        cx={contact.x}
        cy={contact.y}
        r={2}
        fill={teamColor}
        opacity={0.9}
      />
    );
  }

  // Quadratic Bézier bulging perpendicular to the line of travel.
  const dx = landing.x - contact.x;
  const dy = landing.y - contact.y;
  const len = Math.hypot(dx, dy) || 1;
  const off = Math.min(len * profile.bulge, 30);
  const mid = {
    x: (contact.x + landing.x) / 2,
    y: (contact.y + landing.y) / 2,
  };
  const ctrl = { x: mid.x + (dy / len) * off, y: mid.y - (dx / len) * off };
  const path = `M ${contact.x} ${contact.y} Q ${ctrl.x} ${ctrl.y} ${landing.x} ${landing.y}`;

  const cat = classifyShot(shot);
  const color = RESULT_COLORS[cat];
  const r = 2.7;

  return (
    <g>
      {/* struck-from origin */}
      <motion.circle
        cx={contact.x}
        cy={contact.y}
        r={1.5}
        fill={teamColor}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.95 }}
        transition={{ duration: 0.18 }}
      />
      {/* trajectory — drawn behind the ball via shared arc-length progress */}
      <motion.path
        ref={pathRef}
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={0.9}
        strokeLinecap="round"
        opacity={0.5}
        style={{ pathLength: progress }}
      />
      {/* tennis ball */}
      <motion.g style={{ x: ballX, y: ballY, opacity: ballOpacity }}>
        <g transform="translate(-2.6 -2.6)">
          <TennisBall
            size={5.2}
            backgroundColor="#d9e85a"
            strokeWidth={2.2}
            style={{ color: '#0a1228' }}
          />
        </g>
      </motion.g>
      {/* landing marker — pops when the ball arrives */}
      <motion.g
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          delay: profile.duration,
          duration: 0.26,
          ease: 'backOut',
        }}
        style={{ transformOrigin: `${landing.x}px ${landing.y}px` }}
      >
        {cat === 'error' ? (
          <g stroke={color} strokeWidth={1.2} strokeLinecap="round">
            <line
              x1={landing.x - r}
              y1={landing.y - r}
              x2={landing.x + r}
              y2={landing.y + r}
            />
            <line
              x1={landing.x - r}
              y1={landing.y + r}
              x2={landing.x + r}
              y2={landing.y - r}
            />
          </g>
        ) : (
          <circle
            cx={landing.x}
            cy={landing.y}
            r={r}
            fill={cat === 'interception' ? 'none' : color}
            stroke={color}
            strokeWidth={cat === 'interception' ? 1 : 0.6}
          />
        )}
      </motion.g>
    </g>
  );
};

// ── portrait court chrome ──────────────────────────────────────────────────
const CourtFrame: React.FC = () => {
  const line = 'var(--court-line)';
  const x0 = M;
  const x1 = M + CW;
  const y0 = M;
  const y1 = M + 200;
  const cx = M + CW / 2;
  const svcTop = y0 + 30;
  const svcBot = y1 - 30;
  return (
    <g>
      <rect x={x0} y={y0} width={CW} height={200} fill="url(#simSurface)" />
      <rect
        x={x0}
        y={y0}
        width={CW}
        height={200}
        fill="none"
        stroke={line}
        strokeWidth={1}
      />
      <line
        x1={x0}
        y1={svcTop}
        x2={x1}
        y2={svcTop}
        stroke={line}
        strokeWidth={0.7}
      />
      <line
        x1={x0}
        y1={svcBot}
        x2={x1}
        y2={svcBot}
        stroke={line}
        strokeWidth={0.7}
      />
      <line
        x1={cx}
        y1={svcTop}
        x2={cx}
        y2={NET_Y}
        stroke={line}
        strokeWidth={0.7}
      />
      <line
        x1={cx}
        y1={NET_Y}
        x2={cx}
        y2={svcBot}
        stroke={line}
        strokeWidth={0.7}
      />
      <line
        x1={x0}
        y1={NET_Y}
        x2={x1}
        y2={NET_Y}
        stroke={line}
        strokeWidth={1.4}
      />
      <circle cx={x0} cy={NET_Y} r={1.4} fill={line} />
      <circle cx={x1} cy={NET_Y} r={1.4} fill={line} />
    </g>
  );
};

interface ShotSimulatorProps {
  player: SimPlayer;
  subtitle: string;
  shotType: string;
  setShotType: (s: string) => void;
  onClose: () => void;
}

const ShotSimulator: React.FC<ShotSimulatorProps> = ({
  player,
  subtitle,
  shotType,
  setShotType,
  onClose,
}) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;

  const shots = useMemo(
    () => buildShots(player.ps, shotType),
    [player.ps, shotType],
  );
  const [index, setIndex] = useState(0);
  // Reset to the first shot when the player or filter changes.
  useEffect(() => setIndex(0), [player.id, shotType, shots.length]);

  const total = shots.length;
  const current = shots[Math.min(index, Math.max(0, total - 1))];

  const go = useCallback(
    (delta: number) =>
      setIndex((i) => clamp(i + delta, 0, Math.max(0, total - 1))),
    [total],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const shotKey = `${player.id}-${shotType}-${index}`;

  return (
    <section className="sim-inline">
      <div className="court-header">
        <div className="court-header-left">
          <div className="court-header-name">{player.name.toUpperCase()}</div>
          <div className="court-header-sub">{subtitle}</div>
        </div>
        <div className="sim-head-right">
          <span className="sim-counter">
            {total ? `${index + 1} / ${total}` : '0 / 0'}
          </span>
          <button
            type="button"
            className="sim-close"
            onClick={onClose}
            aria-label={t('sim.exit') as string}
          >
            <X size={17} strokeWidth={2.2} />
          </button>
        </div>
      </div>

      <div className="sim-stage">
        <motion.div
          className="sim-court-rot"
          initial={{ rotate: -90, scale: 0.5, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          exit={{ rotate: -90, scale: 0.5, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        >
          <svg
            viewBox={`0 0 ${VW} ${VH}`}
            className="sim-court-svg"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="simSurface" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--court-1)" />
                <stop offset="100%" stopColor="var(--court-2)" />
              </linearGradient>
            </defs>
            <CourtFrame />
            <AnimatePresence mode="wait">
              {current && (
                <motion.g
                  key={shotKey}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.16 }}
                >
                  <ShotArc
                    sim={current}
                    side={player.side}
                    teamColor={player.color.solid}
                  />
                </motion.g>
              )}
            </AnimatePresence>
          </svg>

          <AnimatePresence mode="wait">
            {current && (
              <motion.div
                key={`marker-${shotKey}`}
                className="sim-marker"
                style={{
                  left: `${
                    (latX(player.side, current.shot.player_position_mirroed.x) /
                      VW) *
                    100
                  }%`,
                  top: `${
                    (ownDepthY(
                      player.side,
                      current.shot.player_position_mirroed.y,
                    ) /
                      VH) *
                    100
                  }%`,
                }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={{ duration: 0.22, ease: 'backOut' }}
              >
                <span
                  className="sim-marker-icon"
                  style={{ borderColor: player.color.solid }}
                >
                  {player.crop ? (
                    <img src={player.crop} alt={player.name} />
                  ) : (
                    <User size={18} strokeWidth={2.3} />
                  )}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {total === 0 && (
          <div className="sim-empty">{t('sim.noShots') as string}</div>
        )}
      </div>

      <div className="sim-controls">
        <div className="sim-chips" role="tablist">
          {SHOT_TYPES.map((s) => {
            const active = shotType === s.key;
            return (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`chip${active ? ' active' : ''}`}
                onClick={() => setShotType(s.key)}
              >
                <span className="chip-label">{tl(s.lbl).toUpperCase()}</span>
                <span className="chip-num">{countOf(player.ps, s.key)}</span>
              </button>
            );
          })}
        </div>
        <div className="sim-nav">
          <button
            type="button"
            className="sim-arrow"
            onClick={() => go(-1)}
            disabled={index <= 0}
            aria-label="previous"
          >
            <ChevronLeft size={22} strokeWidth={2.4} />
          </button>
          <div className="sim-shot-label">
            {current ? classLabel(current, tl) : '—'}
          </div>
          <button
            type="button"
            className="sim-arrow"
            onClick={() => go(1)}
            disabled={index >= total - 1}
            aria-label="next"
          >
            <ChevronRight size={22} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </section>
  );
};

function classLabel(sim: SimShot, tl: (k: string) => string): string {
  const cat = classifyShot(sim.shot);
  const key =
    cat === 'bounce'
      ? 'bounces'
      : cat === 'interception'
        ? 'intercept'
        : 'errors';
  return tl(key);
}

export default ShotSimulator;
